import { useCallback, useEffect, useMemo, useState } from 'react';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { usePersistFn, usePrevious, useUpdateEffect } from '@umijs/hooks';
import merge from 'deepmerge';

import { move, reorder } from '../utils';
import map from 'lodash/map';
import uniqueId from 'lodash/uniqueId';
import set from 'lodash/set';

const id2List = {
  droppable: 'items',
  droppable2: 'selected',
};

let saveTimeoutId = null;
const queryAttr = 'data-rbd-drag-handle-draggable-id';

const combineMerge = (target, source, options) => {
  const destination = target.slice();

  source.forEach((item, index) => {
    if (typeof destination[index] === 'undefined') {
      destination[index] = options.cloneUnlessOtherwiseSpecified(item, options);
    } else if (options.isMergeableObject(item)) {
      destination[index] = merge(target[index], item, options);
    } else if (target.indexOf(item) === -1) {
      destination.push(item);
    }
  });
  return destination;
};

const rebuildIds = (list) => {
  return map(list, (item, index) => {
    if (!item.uId) {
      return {
        ...item,
        uId: item.id + '-' + uniqueId(),
      };
    }
    return item;
  });
};

const usePlaylistDrag = (
  pageId,
  contents,
  playlistContents,
  calcPlaylistDuration,
  onSave
) => {
  const [placeholderProps, setPlaceholderProps] = useState({});
  const [state, setState] = useState(rebuildIds(playlistContents));
  const prevPlaylistContents = usePrevious(playlistContents);

  const duration = useMemo(() => {
    return calcPlaylistDuration(state);
  }, [state]);

  const onChangeItemDuration = usePersistFn((id, index, value) => {
    const newState = state.slice();
    set(newState, [index, 'duration'], value);
    setState(() => newState);
    onSave(newState);
  });
  const onDelete = usePersistFn((id, index) => {
    const newState = state.slice();
    newState.splice(index, 1);
    setState(() => newState);
    onSave(newState);
  });

  const onDragStart = useCallback(() => {
    if (window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }
  }, []);
  const onDragUpdate = useCallback((update) => {
    if (!update.destination) {
      return;
    }
    const draggableId = update.draggableId;
    const destinationIndex = update.destination.index;

    const domQuery = `[${queryAttr}='${draggableId}']`;
    const draggedDOM = document.querySelector(domQuery);

    if (!draggedDOM) {
      return;
    }
    const { clientHeight, clientWidth } = draggedDOM;

    const clientY =
      parseFloat(window.getComputedStyle(draggedDOM.parentNode).paddingTop) +
      [...draggedDOM.parentNode.children]
        .slice(0, destinationIndex)
        .reduce((total, curr) => {
          const style = curr.currentStyle || window.getComputedStyle(curr);
          const marginBottom = parseFloat(style.marginBottom);
          return total + curr.clientHeight + marginBottom;
        }, 0);

    if (update.source.droppableId === 'droppable') {
      setPlaceholderProps({
        currentIndex: update.destination.index,
        clientHeight,
        clientWidth,
        clientY,
        clientX: parseFloat(
          window.getComputedStyle(draggedDOM.parentNode).paddingLeft
        ),
      });
    }
  }, []);
  const onDragEnd = useCallback(
    (result) => {
      const { source, destination, draggableId } = result;

      if (!destination) {
        return;
      }

      if (source.droppableId === destination.droppableId) {
        const items = reorder(state, source.index, destination.index);

        if (source.droppableId === 'droppable2') {
          setState(() => items);
          onSave(items);
        }
      } else {
        const result = move(contents, state, source, destination, draggableId);

        if (!isEqual(result.droppable2, state)) {
          setState((oldState) =>
            merge(oldState, rebuildIds(result.droppable2), {
              arrayMerge: combineMerge,
            })
          );

          onSave(result.droppable2);
        }
      }
    },
    [state, contents]
  );

  useUpdateEffect(() => {
    setState((oldState) => {
      let nextState;
      if (!isEqual(prevPlaylistContents, playlistContents)) {
        nextState = rebuildIds(playlistContents);
      } else {
        nextState = merge(oldState, rebuildIds(playlistContents), {
          arrayMerge: combineMerge,
        });
      }
      return nextState;
    });
  }, [playlistContents]);

  return {
    placeholderProps,
    state,
    duration,
    onDragStart,
    onDragUpdate,
    onDragEnd,
    onChangeItemDuration,
    onDelete,
  };
};

export default usePlaylistDrag;
